<template>
  <div class="display">
    <div class="container">
      <!-- 顶部操作栏 -->
      <div class="row mb-4" v-if="showActions">
        <div class="col-md-12">
          <div class="action-bar">
            <button class="btn btn-secondary btn-sm" @click="handleClose" title="关闭">
              <span>← 关闭</span>
            </button>
            <div class="action-buttons">
              <router-link 
                class="btn btn-info btn-sm" 
                :to="{path:'/', query:{id:diary.diaryId}}"
                title="编辑此日记"
              >
                ✏️ 编辑
              </router-link>
              <button 
                class="btn btn-warning btn-sm ms-2" 
                @click="handleDelete"
                title="删除此日记"
              >
                🗑️ 删除
              </button>
              <button 
                class="btn btn-outline-secondary btn-sm ms-2" 
                @click="handleShare"
                title="分享此日记"
              >
                📤 分享
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 日记内容 -->
      <div class="row">
        <div class="col-md-12 text-center">
          <h2 class="diary-title" v-html="getFormattedTitle()"></h2>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-12 text-center diary-meta">
          <span class="badge bg-primary" v-if="diary.tags">{{diary.tags}}</span>
          <span class="text-muted" v-else>无标签</span>
          <span class="separator">|</span>
          <span class="time-info">
            创建：{{getFormattedDate(diary.addTime)}}
          </span>
          <span class="separator">|</span>
          <span class="time-info">
            修改：{{getFormattedDate(diary.lastEditTime)}}
          </span>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div id="diary_content" class="diary-content" v-html="getFormattedContent()"></div>
        </div>
      </div>

      <!-- 底部信息 -->
      <div class="row mt-5" v-if="showFooter">
        <div class="col-md-12">
          <hr />
          <div class="diary-footer text-center text-muted">
            <p class="mb-0">
              此日记创建于 {{getRelativeTimeStr(diary.addTime)}}
              <span v-if="diary.addTime !== diary.lastEditTime">
                ，最后修改于 {{getRelativeTimeStr(diary.lastEditTime)}}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
  .display {
    padding: 20px 0;
  }

  .action-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);

    .action-buttons {
      display: flex;
      gap: 8px;
    }
  }

  .diary-title {
    font-size: 2rem;
    font-weight: 700;
    color: #333;
    margin: 20px 0;
    line-height: 1.4;
  }

  .diary-meta {
    font-size: 0.95rem;
    color: #666;
    padding: 15px 0;

    .badge {
      padding: 6px 12px;
      font-size: 0.9rem;
    }

    .separator {
      margin: 0 12px;
      color: #ccc;
    }

    .time-info {
      font-size: 0.9rem;
    }
  }

  .diary-content {
    text-align: left;
    padding: 30px 0;
    font-size: 1.1rem;
    line-height: 1.8;
    color: #444;
    min-height: 200px;

    // 图片和视频样式
    img, video {
      max-width: 80%;
      max-height: 60vh;
      margin: 20px auto;
      display: block;
      border-radius: 8px;
      box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    }

    // 代码块样式
    pre {
      background-color: #f5f5f5;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 15px;
      overflow-x: auto;
      margin: 20px 0;

      code {
        font-family: 'Courier New', Courier, monospace;
        font-size: 0.9rem;
        color: #333;
      }
    }

    // 段落样式
    p {
      margin-bottom: 1em;
    }

    // 关键字高亮
    span[style*="font-weight:bold"] {
      padding: 2px 4px;
      border-radius: 3px;
      background-color: #fff3cd;
    }
  }

  .diary-footer {
    padding: 20px 0;
    font-size: 0.9rem;

    p {
      margin: 0;
    }
  }

  .mb-3 {
    margin-bottom: 1rem;
  }

  .mb-4 {
    margin-bottom: 1.5rem;
  }

  .mt-5 {
    margin-top: 3rem;
  }

  .ms-2 {
    margin-left: 0.5rem;
  }

  .mb-0 {
    margin-bottom: 0;
  }

  // 响应式设计
  @media (max-width: 768px) {
    .action-bar {
      flex-direction: column;
      gap: 10px;

      .action-buttons {
        width: 100%;
        justify-content: center;
      }
    }

    .diary-title {
      font-size: 1.5rem;
    }

    .diary-content {
      font-size: 1rem;

      img, video {
        max-width: 100%;
      }
    }
  }
</style>

<script>
import { formatDate, getRelativeTime } from '@/utils/dateFormat';
import { formatTextForHtml } from '@/utils/textFormat';
import { API_ENDPOINTS, TOAST_TYPES, CONFIRM_CONFIG } from '@/utils/constants';

export default {
  name: 'DisPlay',
  
  props: {
    diary: {
      type: Object,
      required: true
    },
    keyWord: {
      type: String,
      default: ''
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    }
  },
  
  methods: {
    /**
     * 格式化标题
     */
    getFormattedTitle() {
      return formatTextForHtml(this.diary.title, {
        keyword: this.keyWord,
        limit: 0,
        replaceTag: false
      });
    },
    
    /**
     * 格式化内容
     */
    getFormattedContent() {
      return formatTextForHtml(this.diary.content, {
        keyword: this.keyWord,
        limit: 0,
        replaceTag: true
      });
    },
    
    /**
     * 格式化日期
     */
    getFormattedDate(date) {
      return formatDate(date, false);
    },
    
    /**
     * 获取相对时间
     */
    getRelativeTimeStr(date) {
      return getRelativeTime(date);
    },
    
    /**
     * 关闭窗口
     */
    handleClose() {
      // 如果是新标签页打开的，关闭窗口
      if (window.history.length <= 1) {
        window.close();
      } else {
        // 否则返回上一页
        this.$router.back();
      }
    },
    
    /**
     * 删除日记
     */
    async handleDelete() {
      const confirmed = confirm(CONFIRM_CONFIG.DELETE_DIARY.message);
      
      if (!confirmed) return;
      
      try {
        await this.$myAxios(this, {
          method: 'POST',
          url: `${API_ENDPOINTS.DIARY_DELETE}?id=${this.diary.diaryId}`
        }, (res) => {
          this.$toast(res.msg || '删除成功', TOAST_TYPES.SUCCESS);
          
          // 删除成功后关闭页面或返回列表
          setTimeout(() => {
            this.handleClose();
          }, 1000);
        }, (error) => {
          console.error('删除日记失败:', error);
          this.$toast('删除失败，请重试', TOAST_TYPES.ERROR);
        });
      } catch (error) {
        console.error('删除日记异常:', error);
        this.$toast('删除异常', TOAST_TYPES.ERROR);
      }
    },
    
    /**
     * 分享日记
     */
    handleShare() {
      // 复制当前页面URL到剪贴板
      const url = window.location.href;
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          this.$toast('链接已复制到剪贴板', TOAST_TYPES.SUCCESS);
        }).catch(err => {
          console.error('复制失败:', err);
          this.fallbackCopyToClipboard(url);
        });
      } else {
        this.fallbackCopyToClipboard(url);
      }
    },
    
    /**
     * 备用复制方法
     */
    fallbackCopyToClipboard(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          this.$toast('链接已复制到剪贴板', TOAST_TYPES.SUCCESS);
        } else {
          this.$toast('复制失败，请手动复制', TOAST_TYPES.ERROR);
        }
      } catch (err) {
        console.error('复制失败:', err);
        this.$toast('复制失败，请手动复制：' + text, TOAST_TYPES.ERROR);
      }
      
      document.body.removeChild(textArea);
    }
  },
  
  mounted() {
    // 组件挂载后可以执行一些初始化操作
    console.log('日记详情已加载:', this.diary.title);
  }
};
</script>
